import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import './css/Escola.css';
import Layout from './components/Layout';
import LoadingModal from './components/modals/LoadingModal';
import useDefaultModal from './components/hooks/useDefaultModal';
import DefaultModal from './components/modals/DefaultModal';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import { fndeService } from './api/fndeService';
import ExportarReceitaFndeModal from './components/modals/ExportarReceitaFndeModal';
import useReferenceData from './components/hooks/useReferenceData';
import { API_BASE_URL } from '../config/ApiConfig';

function DadosFnde() {
  const token = localStorage.getItem('token');
  const [dados, setDados] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [consultaRealizada, setConsultaRealizada] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const escola = JSON.parse(localStorage.getItem('escola') || '{}');
  const anoBase = localStorage.getItem('ano') || new Date().getFullYear().toString();
  const cnpj = escola?.cnpj || '';
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { programas, acoes, contas } = useReferenceData(token, cnpj);

  useEffect(() => {
    if (!cnpj) {
      showDefaultModal('warning', 'CNPJ não encontrado. Selecione uma escola.');
      return;
    }
    consultar();
  }, []);

  const consultar = async () => {
    setLoading(true);
    setConsultaRealizada(true);
    try {
      const response = await fndeService.buscarSituacaoAtendimento(anoBase, cnpj);
      if (response.data.sucesso) {
        setDados(response.data.dados || []);
      } else {
        showDefaultModal('error', `Erro: ${response.data.erro}`);
        setDados([]);
      }
    } catch (error) {
      showDefaultModal('error', `Erro ao consultar dados FNDE: ${error.message}`);
      setDados([]);
    } finally {
      setLoading(false);
    }
  };

  const parseBRL = (v) => {
    if (!v) return null;
    const s = String(v).replace(/\./g, '').replace(',', '.');
    const n = parseFloat(s);
    return isNaN(n) ? null : n;
  };

  const resumoPorPrograma = useMemo(() => {
    const map = {};
    (Array.isArray(dados) ? dados : []).forEach(linha => {
      const nome = String(linha['Programa'] || '-');
      const custeio = parseBRL(linha['Valor Custeio']) || 0;
      const capital = parseBRL(linha['Valor Capital']) || 0;
      if (!map[nome]) map[nome] = { nome, custeio: 0, capital: 0, qtd: 0 };
      map[nome].custeio += custeio;
      map[nome].capital += capital;
      map[nome].qtd += 1;
    });
    return Object.values(map);
    // eslint-disable-next-line
  }, [dados]);

  const handleExportarReceita = async (linhas) => {
    setLoading(true);
    try {
      await Promise.all(linhas.map(l =>
        axios.post(`${API_BASE_URL.RECEITA}/save`, {
          ano: parseInt(anoBase),
          idPrograma: parseInt(l.idPrograma),
          idAcao: parseInt(l.idAcao),
          idEscola: cnpj,
          idConta: l.idConta,
          destinacao: l.destinacao || '',
          vlrC: parseBRL(l.vlrC),
          vlrK: parseBRL(l.vlrK),
          data: l.data || null,
        }, { headers: { Authorization: `Bearer ${token}` } })
      ));
      setShowExportModal(false);
      showDefaultModal('success', `${linhas.length} receita(s) exportada(s) com sucesso!`);
    } catch {
      showDefaultModal('error', 'Erro ao exportar receitas.');
    } finally {
      setLoading(false);
    }
  };

  const exportarCSV = () => {
    if (dados.length === 0) {
      showDefaultModal('warning', 'Nenhum dado para exportar.');
      return;
    }

    const headers = Object.keys(dados[0]);
    const csvContent = [
      headers.join(';'),
      ...dados.map(linha =>
        headers.map(header => {
          const valor = linha[header] || '';
          // Envolver em aspas se contiver ponto-e-vírgula, aspas ou quebra de linha
          return typeof valor === 'string' && (valor.includes(';') || valor.includes('"') || valor.includes('\n'))
            ? `"${valor.replace(/"/g, '""')}"`
            : valor;
        }).join(';')
      )
    ].join('\n');

    // BOM UTF-8 para Excel reconhecer acentuação corretamente
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `fnde_situacao_atendimento_${anoBase}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <DefaultModal {...setDefaultModal} />
      <ExportarReceitaFndeModal
        show={showExportModal}
        onClose={() => setShowExportModal(false)}
        onSubmit={handleExportarReceita}
        dados={dados}
        programas={programas}
        acoes={acoes}
        contas={contas}
      />
      <ResponsiveShadow>
      <PageHeader 
        title="Consulta FNDE" 
        subtitle="Situação de Atendimento de Entidades - Dados da Secretaria de Educação"
        icon="bi-table"
      />
      
      <PageContent style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

          {/* Cards por programa */}
          {resumoPorPrograma.length > 0 && (
            <div className="mb-2 d-flex flex-wrap gap-2" style={{ maxHeight: '30vh', overflowY: 'auto', overflowX: 'hidden', flexShrink: 0 }}>
              {resumoPorPrograma.map(prog => (
                <div className="card border-0 shadow-sm" key={prog.nome} style={{ borderRadius: 10, flex: '1 1 0', minWidth: 200, maxWidth: '25%' }}>
                  <div className="card-body pt-2 pb-1 px-3">
                    <div className="d-flex justify-content-between align-items-center gap-2" style={{ backgroundColor: '#198754', color: '#fff', margin: '-0.5rem -1rem 0.5rem', padding: '0.15rem 1rem', borderRadius: '10px 10px 0 0', minHeight: '22px' }}>
                      <span className="fw-bold text-truncate" style={{ fontSize: '.62rem' }} title={prog.nome}>{prog.nome}</span>
                      <span className="badge text-bg-light text-success flex-shrink-0" style={{ fontSize: '.62rem' }}>{prog.qtd}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">Custeio</span><span>{prog.custeio.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">Capital</span><span>{prog.capital.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                    </div>
                    <div className="d-flex justify-content-between pt-1 mt-1 border-top fw-bold" style={{ fontSize: '.78rem', whiteSpace: 'nowrap' }}>
                      <span>Total</span><span className="text-success">{(prog.custeio + prog.capital).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Botões de ação */}
          {dados.length > 0 && (
            <div className="d-flex align-items-center gap-2 mb-3">
              <button className="btn btn-success btn-sm" onClick={exportarCSV} disabled={isLoading}>
                <i className="bi bi-download me-2"></i>
                Exportar CSV
              </button>
              <span className="ms-2 text-muted" style={{ fontSize: '0.9rem' }}>
                <strong>{dados.length}</strong> registro(s) | Ano: <strong>{anoBase}</strong> | CNPJ: <strong>{cnpj}</strong>
              </span>
            </div>
          )}

          {/* Tabela de Resultados */}
          {consultaRealizada && (
            <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
              {dados.length > 0 ? (
                <table className="table table-striped table-hover table-sm">
                  <thead className="table-success">
                    <tr>
                      <th>Programa</th>
                      <th>Destinação</th>
                      <th>Valor Custeio</th>
                      <th>Valor Capital</th>
                      <th>Valor Total</th>
                      <th>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dados.map((linha, index) => {
                      const valorCusteio = parseBRL(linha['Valor Custeio']);
                      const valorCapital = parseBRL(linha['Valor Capital']);
                      const total = [valorCusteio || 0, valorCapital || 0].reduce((a, b) => a + b, 0);
                      return (
                        <tr key={index}>
                          <td>{linha['Programa'] || '-'}</td>
                          <td>{linha['Destinação'] || '-'}</td>
                          <td className="text-end">{valorCusteio !== null ? valorCusteio.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '-'}</td>
                          <td className="text-end">{valorCapital !== null ? valorCapital.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '-'}</td>
                          <td className="fw-bold text-success text-end">{total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                          <td>{linha['Data da Ord. de Pagamento'] || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="alert alert-info" role="alert">
                  <i className="bi bi-exclamation-circle me-2"></i>
                  Nenhum registro encontrado com os critérios especificados.
                  <br />
                  <small className="text-muted mt-2 d-block">
                    Verifique o ano, CNPJ ou tente novamente mais tarde.
                  </small>
                </div>
              )}
            </div>
          )}

          </PageContent>
        </ResponsiveShadow>
      
    </Layout>
  );
}

export default DadosFnde;
